import React, { useRef, useEffect } from 'react';
import { DataSet, Network } from 'vis-network/standalone/esm/vis-network';

const severityColors = {
  mild:     '#FFD966',
  moderate: '#F4984D',
  severe:   '#E74C3C'
};

export default function MapView({
  medications,
  interactions,
  selected,
  onSelect,
  zoomLevel,
  onZoomIn,
  onZoomOut
}) {
  const container   = useRef(null);
  const networkRef  = useRef(null);

  useEffect(() => {
    // nodos
    const nodes = new DataSet(
      medications.map(med => ({
        id:    med.id,
        label: med.name,
        shape: 'dot',
        size:  med.id === selected?.id ? 25 : 20,
        color: {
          border: med.id === selected?.id ? '#1E40AF' : '#E5E7EB',
          background: (() => {
            const hasSev = interactions.some(i =>
              (i.source === med.id || i.target === med.id) && i.severity === 'severe'
            );
            const hasMod = interactions.some(i =>
              (i.source === med.id || i.target === med.id) && i.severity === 'moderate'
            );
            if (hasSev) return severityColors.severe;
            if (hasMod) return severityColors.moderate;
            return '#8BB9DD';
          })()
        }
      }))
    );

    // aristas
    const edges = new DataSet(
      interactions.map((int, idx) => ({
        id:     idx,
        from:   int.source,
        to:     int.target,
        color:  severityColors[int.severity],
        width:  int.severity === 'severe' ? 4 : int.severity === 'moderate' ? 3 : 2,
        title:  int.description,
        arrows: 'to'
      }))
    );

    const options = {
      physics: false,
      interaction: { hover: true, zoomView: true, dragView: true },
      nodes: { chosen: false, font: { size: 14, color: '#333' } },
      edges: { chosen: false, smooth: { enabled: false } }
    };

    // destruir/redibujar
    if (networkRef.current) {
      networkRef.current.destroy();
      networkRef.current = null;
    }
    networkRef.current = new Network(container.current, { nodes, edges }, options);

    // click en nodo
    networkRef.current.on('click', params => {
      if (params.nodes.length > 0) {
        const id  = params.nodes[0];
        const med = medications.find(m => m.id === id);
        onSelect(med);
      }
    });

    // zoom
    networkRef.current.moveTo({ scale: zoomLevel });
  }, [medications, interactions, selected, zoomLevel, onSelect]);

  return (
    <div className="d-flex flex-column h-100">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h6 className="mb-0">Visualización de Interacciones</h6>
        <div className="btn-group">
          <button className="btn btn-light btn-sm" onClick={onZoomIn}>➕</button>
          <button className="btn btn-light btn-sm" onClick={onZoomOut}>➖</button>
          <button className="btn btn-light btn-sm" onClick={() => onZoomOut(1)}>🔄</button>
        </div>
      </div>
      <div
        ref={container}
        style={{ flex: 1, border: '1px solid #e2e8f0', borderRadius: 6, minHeight: 300 }}
      />
      {selected && (
        <div className="border mt-3 p-3 rounded" style={{ backgroundColor: '#E4ECFA', borderColor: '#D8B4FE' }}>
          <h6>{selected.name} – Interacciones</h6>
          {interactions
            .filter(i => i.source === selected.id || i.target === selected.id)
            .map((i, idx) => {
              const otherId = i.source === selected.id ? i.target : i.source;
              const other   = medications.find(m => m.id === otherId);
              return (
                <div key={idx} className="d-flex align-items-start mb-2">
                  <span
                    className="me-2"
                    style={{
                      display: 'inline-block',
                      width: 12, height: 12,
                      background: severityColors[i.severity],
                      borderRadius: '50%',
                      marginTop: 4
                    }}
                  />
                  <div>
                    <strong>Interacción con {other.name}</strong>
                    <div className="small text-muted">{i.description}</div>
                  </div>
                </div>
              );
            })}
        </div>
      )}
    </div>
  );
}
